import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Inbox, CheckCircle, Search, Filter, Zap, ArrowRight, Loader2, Eye, RefreshCw } from 'lucide-react';

const STATUS_LABELS = {
  order_received: 'Order Received', in_review: 'In Review',
  more_images_requested: 'More Images', images_received: 'Images Received',
  authentic: 'Authentic', counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Done', unable_to_determine: 'Unable',
  refunded: 'Refunded', revoked: 'Revoked',
};

const STATUS_COLORS = {
  order_received: 'bg-amber-50 text-amber-700 border-amber-200',
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  more_images_requested: 'bg-orange-50 text-orange-700 border-orange-200',
  images_received: 'bg-sky-50 text-sky-700 border-sky-200',
  authentic: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  counterfeit: 'bg-red-50 text-red-700 border-red-200',
  appraisal_complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unable_to_determine: 'bg-gray-50 text-gray-700 border-gray-200',
  refunded: 'bg-red-50 text-red-600 border-red-200',
  revoked: 'bg-red-50 text-red-800 border-red-300',
};

const PROVIDER_LABELS = {
  stripe: { label: 'Stripe', bg: 'rgba(99,91,255,0.10)', color: '#635BFF' },
  paypal: { label: 'PayPal', bg: 'rgba(0,48,135,0.08)', color: '#003087' },
  mock: { label: 'Mock', bg: 'rgba(107,100,92,0.10)', color: '#6B645C' },
};

export default function QueuePage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const tab = params.get('tab') || 'new';
  const filterCategory = params.get('category') || '';
  const filterStatus = params.get('status') || '';
  const filterExpedited = params.get('expedited') || '';
  const filterService = params.get('service_type') || '';
  const page = parseInt(params.get('page') || '1');
  const limit = 100;

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ tab, page: String(page), limit: String(limit) });
      if (filterCategory) qp.set('category', filterCategory);
      if (filterStatus) qp.set('status', filterStatus);
      if (filterExpedited) qp.set('expedited', filterExpedited);
      if (filterService) qp.set('service_type', filterService);
      if (searchText) qp.set('search', searchText);
      const res = await api.get(`/team/queue?${qp}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [tab, page, filterCategory, filterStatus, filterExpedited, filterService, searchText]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Changing the tab, a filter, or the search resets to page 1 so the new
  // result set always starts at the beginning (mirrors UsersPage behavior).
  const setFilter = (key, val) => {
    const newParams = new URLSearchParams(params);
    if (val) newParams.set(key, val); else newParams.delete(key);
    if (key !== 'page') newParams.delete('page');
    setParams(newParams);
  };

  const goToPage = (p) => {
    const newParams = new URLSearchParams(params);
    newParams.set('page', String(p));
    setParams(newParams);
  };

  // Applying a search resets to page 1 (search lives in local state, not the
  // URL, so we drop any existing page param before reloading).
  const runSearch = () => {
    if (page !== 1) {
      const newParams = new URLSearchParams(params);
      newParams.delete('page');
      setParams(newParams);
    } else {
      loadQueue();
    }
  };

  return (
    <div data-testid="team-queue-page">
      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <Tabs value={tab} onValueChange={v => setFilter('tab', v === 'new' ? null : v)}>
          <TabsList className="h-9">
            <TabsTrigger value="new" className="text-xs gap-1.5" data-testid="tab-new">
              <Inbox className="h-3.5 w-3.5" /> New
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-xs gap-1.5" data-testid="tab-completed">
              <CheckCircle className="h-3.5 w-3.5" /> Completed
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-xs text-muted-foreground">{total} item{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Filters toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
            placeholder="Search items, serials, orders..."
            className="pl-8 h-8 text-xs"
            data-testid="queue-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={v => setFilter('category', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="handbags">Handbags</SelectItem>
            <SelectItem value="watches">Watches</SelectItem>
            <SelectItem value="shoes">Shoes</SelectItem>
            <SelectItem value="clothing">Clothing</SelectItem>
            <SelectItem value="accessories">Accessories</SelectItem>
            <SelectItem value="jewelry">Jewelry</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterService} onValueChange={v => setFilter('service_type', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            <SelectItem value="authentication">Authentication</SelectItem>
            <SelectItem value="appraisal">Appraisal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterExpedited} onValueChange={v => setFilter('expedited', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-28 text-xs"><SelectValue placeholder="Expedited" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Expedited Only</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={runSearch}>
          <Filter className="h-3 w-3 mr-1" /> Apply
        </Button>
      </div>

      <Separator className="mb-4" />

      {/* Queue table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-white border-stone/50">
          <CardContent className="p-8 text-center">
            <Inbox className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No items in this queue.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {items.map(item => (
            <Link key={item.id} to={`/team/items/${item.id}`} className="block no-underline" data-testid={`queue-item-${item.id}`}>
              <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors duration-150 hover:bg-white/80 ${
                item.opened_by_staff ? 'bg-white/50 border-stone/30' : 'bg-white border-stone/50 shadow-sm'
              }`}>
                {/* Opened indicator */}
                <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${
                  item.opened_by_staff ? 'bg-stone/30' : 'bg-ink'
                }`} />

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-ink truncate">{item.item_title || 'Untitled Item'}</span>
                    {item.is_expedited && (
                      <Badge className="text-[9px] h-4 px-1.5 gap-0.5 flex-shrink-0" style={{ background: 'rgba(163,106,58,0.12)', color: '#A36A3A', border: '1px solid rgba(163,106,58,0.25)' }}>
                        <Zap className="h-2.5 w-2.5" /> Expedited
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>{item.brand_name || 'No brand'}</span>
                    <span>-</span>
                    <span>{item.category_name}</span>
                    <span>-</span>
                    <span>{item.service_name}</span>
                    {item.order_number && <><span>-</span><span className="font-mono">{item.order_number}</span></>}
                  </div>
                </div>

                {/* Status */}
                <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${STATUS_COLORS[item.status] || ''}`}>
                  {STATUS_LABELS[item.status] || item.status}
                </Badge>

                {/* Refunded pill — surfaces a money-returned marker even on
                    open items (staff refunds no longer close the item). */}
                {item.refunded_cents > 0 && (
                  <Badge
                    className="text-[9px] h-4 px-1.5 gap-0.5 flex-shrink-0 hidden md:inline-flex"
                    style={{ background: 'rgba(217,119,6,0.12)', color: '#B45309', border: '1px solid rgba(217,119,6,0.25)' }}
                    title={`Refunded $${(item.refunded_cents / 100).toFixed(2)}`}
                    data-testid={`refunded-badge-${item.id}`}
                  >
                    <RefreshCw className="h-2.5 w-2.5" />
                    ${(item.refunded_cents / 100).toFixed(2)}
                  </Badge>
                )}

                {/* Payment provider (PROV-01) — only rendered when known */}
                {item.payment_provider && PROVIDER_LABELS[item.payment_provider] && (
                  <Badge
                    variant="outline"
                    className="text-[9px] flex-shrink-0 hidden md:inline-flex"
                    style={{
                      background: PROVIDER_LABELS[item.payment_provider].bg,
                      color: PROVIDER_LABELS[item.payment_provider].color,
                      borderColor: PROVIDER_LABELS[item.payment_provider].color + '40',
                    }}
                    data-testid={`provider-badge-${item.payment_provider}`}
                  >
                    {PROVIDER_LABELS[item.payment_provider].label}
                  </Badge>
                )}

                {/* Customer */}
                <span className="text-[11px] text-muted-foreground truncate max-w-[140px] hidden lg:block">
                  {item.customer_email || ''}
                </span>

                {/* Date */}
                <span className="text-[11px] text-muted-foreground flex-shrink-0 hidden sm:block">
                  {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                </span>

                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination — page through all items beyond the first page (QUE-01) */}
      {total > limit && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="queue-pagination">
          <Button variant="outline" size="sm" className="text-xs" disabled={page <= 1}
            onClick={() => goToPage(page - 1)} data-testid="queue-prev">Previous</Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {Math.max(1, Math.ceil(total / limit))}
          </span>
          <Button variant="outline" size="sm" className="text-xs" disabled={page >= Math.ceil(total / limit)}
            onClick={() => goToPage(page + 1)} data-testid="queue-next">Next</Button>
        </div>
      )}
    </div>
  );
}
